import invoicesService from '@/services/invoices';
import paymentPlansService from '@/services/paymentPlans';
import accountService from '@/services/account';
import customersService from '@/services/customers';
import generalCatalogsService from '@/services/generalCatalogs';
import { extractApiErrorMessage, extractCollection, extractPayload } from '@/utils/dataNormalization';
import { mapCustomerOption, mapSerialOption, normalizeFiscalCodeOptions } from '@/composables/invoices/create/helpers';

const GLOBAL_PUBLIC_RFC = 'XAXX010101000';

const normalizeGlobalInvoicePeriodicityOptions = (items) => {
  return (Array.isArray(items) ? items : [])
    .map((item) => {
      const code = String(item?.code || '').trim();
      const name = String(item?.name || '').trim();

      if (code === '' || name === '') {
        return null;
      }

      return {
        value: code,
        label: name,
        meta: {
          ...item,
          code,
          name,
        },
      };
    })
    .filter(Boolean);
};

export const invoiceCreateMethods = {
  async initializePage() {
    await this.loadBillingData();
    const cloneApplied = await this.applyInvoiceClonePrefillIfNeeded();
    if (cloneApplied) {
      return;
    }

    await this.applyRouteReceiverPrefillIfNeeded();
    await this.applyPaymentPlanPrefillIfNeeded();
  },
  resolveCloneInvoiceId() {
    const cloneInvoiceId = Number(
      this.$route?.query?.clone_invoice_id
      || this.$route?.query?.cloneInvoiceId
      || 0
    );

    return Number.isFinite(cloneInvoiceId) && cloneInvoiceId > 0 ? Math.floor(cloneInvoiceId) : 0;
  },
  normalizeCloneConcepts(concepts = []) {
    return (Array.isArray(concepts) ? concepts : [])
      .map((concept) => {
        const quantity = Number(concept?.qty || concept?.quantity || 0) || 0;
        const unitPrice = Number(concept?.unitPrice || concept?.price || 0) || 0;
        const taxTransferredTotal = Number(concept?.taxTransferredTotal || concept?.taxes?.transferred_total || 0) || 0;
        const taxWithheldTotal = Number(concept?.taxWithheldTotal || concept?.taxes?.withheld_total || 0) || 0;

        return {
          serviceId: Number(concept?.serviceId || concept?.productId || 0) || 0,
          productId: Number(concept?.productId || concept?.serviceId || 0) || 0,
          paymentId: 0,
          saleId: Number(concept?.saleId || concept?.sale_id || 0) || 0,
          qty: quantity > 0 ? quantity : 1,
          unit: String(concept?.unit || '').trim(),
          unitValue: String(concept?.unitValue || concept?.unitCode || '').trim(),
          unitCode: String(concept?.unitCode || concept?.unitValue || '').trim(),
          unitId: Number(concept?.unitId || 0) || 0,
          code: String(concept?.code || concept?.sat_code || '').trim(),
          description: String(concept?.description || '').trim(),
          unitPrice,
          taxLabel: String(concept?.taxLabel || '').trim() || this.generalText.financial.taxes.defaultTransferredLabel,
          taxAmount: Number(concept?.taxAmount || 0) || 0,
          taxTransferredTotal,
          taxWithheldTotal,
          taxId: Number(concept?.taxId || 0) || 0,
          taxGroupId: Number(concept?.taxGroupId || 0) || 0,
          sourceMode: 'manual',
          sourcePaymentIds: [],
          isPaymentPlanMerged: false,
          taxes: concept?.taxes && typeof concept.taxes === 'object'
            ? { ...concept.taxes }
            : {
              transferred_total: taxTransferredTotal,
              withheld_total: taxWithheldTotal,
              items: [],
            },
        };
      })
      .filter((concept) => {
        return String(concept?.description || '').trim() !== ''
          || Number(concept?.unitPrice || 0) > 0
          || Number(concept?.productId || 0) > 0
          || Number(concept?.saleId || 0) > 0;
      });
  },
  async applyInvoiceClonePrefillIfNeeded() {
    const cloneInvoiceId = this.resolveCloneInvoiceId();
    if (cloneInvoiceId <= 0) {
      return false;
    }

    this.prefillPaymentId = 0;
    this.prefillPaymentCustomerId = 0;
    this.receiverLocked = false;

    try {
      const response = await invoicesService.show(cloneInvoiceId, {}, { skipLoading: true });
      const payload = extractPayload(response);
      const sourceInvoice = payload && typeof payload === 'object' && payload.data && typeof payload.data === 'object'
        ? payload.data
        : payload;

      if (!sourceInvoice || Number(sourceInvoice?.id || 0) <= 0) {
        throw new Error('CLONE_SOURCE_NOT_FOUND');
      }

      const receiverContributorId = Number(
        sourceInvoice?.selectedReceiver?.meta?.id
        || sourceInvoice?.selectedReceiver?.value
        || 0
      ) || 0;
      const receiverCustomerId = Number(
        sourceInvoice?.selectedReceiver?.meta?.customerId
        || sourceInvoice?.selectedReceiver?.customerId
        || sourceInvoice?.company_id
        || sourceInvoice?.customer_id
        || 0
      ) || 0;

      let receiverResolved = false;
      if (receiverContributorId > 0) {
        receiverResolved = await this.ensureReceiverSelectedByContributor(receiverContributorId);
      }

      if (!receiverResolved && receiverCustomerId > 0) {
        receiverResolved = await this.ensureReceiverSelectedByCustomer(receiverCustomerId);
      }

      if (!receiverResolved) {
        this.$toast?.warning(this.text.create.toast.prefillReceiverWarning);
      }

      const clonedConcepts = this.normalizeCloneConcepts(sourceInvoice?.concepts);
      if (clonedConcepts.length === 0) {
        this.$toast?.warning(this.text.create.validation.conceptsRequired);
      } else {
        this.concepts = clonedConcepts;
      }

      const paymentMethod = String(sourceInvoice?.paymentMethod || this.invoiceInfo?.paymentMethod || 'PPD').trim() || 'PPD';
      const paymentForm = String(sourceInvoice?.paymentForm || this.invoiceInfo?.paymentForm || '99').trim() || '99';

      this.invoiceInfo = {
        ...this.invoiceInfo,
        paymentMethod,
        paymentForm,
      };

      const sourceComments = String(sourceInvoice?.comments || '').trim();
      if (sourceComments !== '') {
        this.comments = sourceComments;
      }

      return true;
    } catch (error) {
      this.$toast?.error(this.text.create.toast.prefillLoadError);
      return false;
    }
  },
  resolvePrefillReceiverId() {
    const receiverId = Number(
      this.$route?.query?.contributor_id
      || this.$route?.query?.contributorId
      || this.$route?.query?.receiver_id
      || this.$route?.query?.receiverId
      || 0
    );

    return Number.isFinite(receiverId) && receiverId > 0 ? Math.floor(receiverId) : 0;
  },
  resolvePrefillCustomerId() {
    const customerId = Number(this.$route?.query?.customer_id || this.$route?.query?.customerId || 0);
    return Number.isFinite(customerId) && customerId > 0 ? Math.floor(customerId) : 0;
  },
  async applyRouteReceiverPrefillIfNeeded() {
    const receiverId = this.resolvePrefillReceiverId();
    if (receiverId > 0) {
      const receiverResolved = await this.ensureReceiverSelectedByContributor(receiverId);
      if (receiverResolved) {
        return true;
      }
    }

    const customerId = this.resolvePrefillCustomerId();
    if (customerId > 0) {
      return this.ensureReceiverSelectedByCustomer(customerId);
    }

    return false;
  },
  async ensureReceiverSelectedByContributor(contributorId) {
    const resolvedContributorId = Number(contributorId || 0) || 0;
    if (resolvedContributorId <= 0) {
      return false;
    }

    const findOption = () => {
      const options = Array.isArray(this.customerOptions) ? this.customerOptions : [];
      return options.find((option) => {
        const optionContributorId = Number(option?.meta?.id || option?.value || 0) || 0;
        return optionContributorId === resolvedContributorId;
      }) || null;
    };

    let receiver = findOption();
    if (!receiver) {
      await this.loadReceiverSelectors();
      receiver = findOption();
    }

    if (!receiver) {
      return false;
    }

    this.selectedReceiver = receiver;
    this.syncGlobalInvoiceByReceiver(receiver);
    return true;
  },
  resolvePrefillPaymentId() {
    const paymentId = Number(this.$route?.query?.payment_id || this.$route?.query?.paymentId || 0);
    return Number.isFinite(paymentId) && paymentId > 0 ? Math.floor(paymentId) : 0;
  },
  async applyPaymentPlanPrefillIfNeeded() {
    const paymentId = this.resolvePrefillPaymentId();
    this.prefillPaymentId = paymentId;
    this.prefillPaymentCustomerId = 0;

    if (paymentId <= 0) {
      this.receiverLocked = false;
      return;
    }

    try {
      const response = await paymentPlansService.show(paymentId, { skipLoading: true });
      const payload = extractPayload(response);
      const payment = payload && typeof payload === 'object' && payload.data && typeof payload.data === 'object'
        ? payload.data
        : payload;

      if (!payment || Number(payment?.id || 0) <= 0) {
        throw new Error('PAYMENT_NOT_FOUND');
      }

      const prefilledConcept = await this.buildPrefilledConceptFromPayment(payment);
      if (prefilledConcept) {
        const conceptPaymentId = Number(prefilledConcept?.paymentId || 0) || 0;
        const hasPrefilledConcept = Array.isArray(this.concepts) && this.concepts.some((concept) => {
          return Number(concept?.paymentId || concept?.payment_id || 0) === conceptPaymentId;
        });

        if (!hasPrefilledConcept) {
          this.concepts = [...this.concepts, prefilledConcept];
        }
      }

        const customerId = Number(payment?.company_id || payment?.customer_id || 0) || 0;
        this.prefillPaymentCustomerId = customerId;
      const receiverResolved = await this.ensureReceiverSelectedByCustomer(customerId);
      this.receiverLocked = receiverResolved;

      if (!receiverResolved) {
        this.$toast?.warning(this.text.create.toast.prefillReceiverWarning);
      }

      const paymentComments = String(payment?.comments || '').trim();
      if (paymentComments !== '') {
        this.comments = paymentComments;
      }
    } catch (error) {
      this.receiverLocked = false;
      this.$toast?.error(this.text.create.toast.prefillLoadError);
    }
  },
  async ensureReceiverSelectedByCustomer(customerId) {
    const resolvedCustomerId = Number(customerId || 0) || 0;
    if (resolvedCustomerId <= 0) {
      return false;
    }

    const findOption = () => {
      const options = Array.isArray(this.customerOptions) ? this.customerOptions : [];
      const matches = options.filter((option) => {
        const optionCustomerId = Number(
          option?.customerId
          || option?.meta?.customerId
          || option?.meta?.companyId
          || 0
        ) || 0;

        return optionCustomerId === resolvedCustomerId;
      });

      if (matches.length === 0) {
        return null;
      }

      const defaultMatch = matches.find((option) => {
        return Number(option?.isDefault || option?.meta?.default || 0) === 1;
      });

      return defaultMatch || matches[0] || null;
    };

    let receiver = findOption();
    if (!receiver) {
      await this.loadReceiverSelectors();
      receiver = findOption();
    }

    if (!receiver) {
      return false;
    }

    this.selectedReceiver = receiver;
    this.syncGlobalInvoiceByReceiver(receiver);
    return true;
  },
  resolveCurrentGlobalExerciseValue(dateValue = null) {
    const normalizedDate = String(
      dateValue
      || this.invoiceInfo?.date
      || ''
    ).trim();

    if (/^\d{4}-\d{2}-\d{2}$/.test(normalizedDate)) {
      return normalizedDate.slice(0, 7);
    }

    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  },
  resolveDefaultGlobalPeriodicityCode() {
    const options = Array.isArray(this.globalInvoicePeriodicityOptions)
      ? this.globalInvoicePeriodicityOptions
      : [];

    const monthly = options.find((option) => String(option?.value || '').trim() === '04');
    if (monthly) {
      return '04';
    }

    return String(options[0]?.value || '04').trim() || '04';
  },
  isPublicGeneralReceiver(receiver = null) {
    const source = receiver && typeof receiver === 'object' ? receiver : this.selectedReceiver;
    const rfc = String(source?.meta?.rfc || source?.rfc || '').trim().toUpperCase();
    return rfc === GLOBAL_PUBLIC_RFC;
  },
  syncGlobalInvoiceByReceiver(receiver = null) {
    const currentExercise = this.resolveCurrentGlobalExerciseValue();
    const defaultPeriodicity = this.resolveDefaultGlobalPeriodicityCode();

    if (!this.isPublicGeneralReceiver(receiver)) {
      this.invoiceInfo = {
        ...this.invoiceInfo,
        isGlobal: 0,
        globalPeriodicity: defaultPeriodicity,
        globalExercise: currentExercise,
      };
      return;
    }

    this.invoiceInfo = {
      ...this.invoiceInfo,
      isGlobal: 0,
      globalPeriodicity: defaultPeriodicity,
      globalExercise: currentExercise,
    };
  },
  async resolveProductPresetForPayment(payment = {}) {
    const requestedProductId = Number(payment?.product_id || 0) || 0;

    try {
      const response = await invoicesService.productSelectors({}, { skipLoading: true });
      const products = extractCollection(response);
      const options = (Array.isArray(products) ? products : [])
        .map((item) => {
          const id = Number(item?.id || 0);
          const label = String(item?.label || '').trim();

          if (id <= 0 || label === '') {
            return null;
          }

          return {
            value: id,
            label,
            meta: item,
          };
        })
        .filter(Boolean);

      if (options.length === 0) {
        return null;
      }

      if (requestedProductId > 0) {
        return options.find((option) => Number(option?.value || 0) === requestedProductId) || options[0];
      }

      return options[0];
    } catch (error) {
      return null;
    }
  },
  async buildPrefilledConceptFromPayment(payment = {}) {
    const paymentId = Number(payment?.id || 0) || 0;
    if (paymentId <= 0) {
      return null;
    }

    const productPreset = await this.resolveProductPresetForPayment(payment);
    const productMeta = productPreset?.meta || {};

    const unitCode = String(productMeta?.unit_code || this.text.create.prefill.defaultUnitCode).trim() || this.text.create.prefill.defaultUnitCode;
    const unitName = String(productMeta?.unit_name || this.text.create.prefill.defaultUnitName).trim() || this.text.create.prefill.defaultUnitName;
    const unitLabel = `${unitCode} - ${unitName}`;
    const satCode = String(productMeta?.sat_product_code || this.text.create.prefill.defaultSatCode).trim() || this.text.create.prefill.defaultSatCode;

    const subtotal = Number(payment?.subtotal || payment?.amount || 0) || 0;
    const taxes = payment?.taxes && typeof payment.taxes === 'object'
      ? payment.taxes
      : { transferred_total: 0, withheld_total: 0, items: [] };
    const taxItems = Array.isArray(taxes?.items) ? taxes.items : [];
    const taxAnchor = taxItems.find((item) => String(item?.direction || '').trim().toLowerCase() === 'trasladado') || taxItems[0] || null;

    const taxName = String(taxAnchor?.name || taxAnchor?.code || this.generalText.financial.taxes.tax).trim() || this.generalText.financial.taxes.tax;
    const rawRate = Number(taxAnchor?.rate || taxAnchor?.percentage || taxAnchor?.percent || 0) || 0;
    const rate = rawRate > 0 && rawRate <= 1 ? rawRate * 100 : rawRate;
    const rateLabel = rate > 0 ? `${String(rate).replace(/\.?0+$/, '')}%` : '';

    const transferredTotal = Number(taxes?.transferred_total || 0) || 0;
    const withheldTotal = Number(taxes?.withheld_total || 0) || 0;

    return {
      paymentId,
      saleId: Number(payment?.sale_id || 0) || 0,
      serviceId: Number(productPreset?.value || payment?.product_id || 0) || 0,
      productId: Number(productPreset?.value || payment?.product_id || 0) || 0,
      qty: 1,
      unit: unitLabel,
      unitValue: unitCode,
      unitCode,
      unitId: Number(productMeta?.unit_id || 0) || 0,
      code: satCode,
      description: String(
        payment?.concept
        || productMeta?.description
        || this.text.create.prefill.defaultConceptDescription
      ).trim() || this.text.create.prefill.defaultConceptDescription,
      unitPrice: subtotal,
      taxLabel: rateLabel !== '' ? `${taxName} ${rateLabel}` : taxName,
      taxAmount: transferredTotal - withheldTotal,
      taxTransferredTotal: transferredTotal,
      taxWithheldTotal: withheldTotal,
      taxId: Number(payment?.tax_id || 0) || 0,
      taxGroupId: Number(payment?.tax_group_id || 0) || 0,
      taxes,
    };
  },
  async loadBillingData() {
    await Promise.all([
      this.loadIssuerTaxInformation(),
      this.loadReceiverSelectors(),
      this.loadFiscalCatalogs(),
      this.loadSerialSelectors(),
    ]);
  },
  async loadFiscalCatalogs() {
    try {
      const [regimensResult, usosResult, formasResult, metodosResult, globalPeriodicitiesResult] = await Promise.allSettled([
        generalCatalogsService.getRegimens(),
        generalCatalogsService.getUsosCfdi(),
        generalCatalogsService.getFormasPago(),
        generalCatalogsService.getMetodosPago(),
        generalCatalogsService.getGlobalInvoicePeriodicities(),
      ]);

      if (regimensResult.status === 'fulfilled') {
        this.taxRegimeOptions = normalizeFiscalCodeOptions(extractCollection(regimensResult.value));
      }

      if (usosResult.status === 'fulfilled') {
        this.cfdiUseOptions = normalizeFiscalCodeOptions(extractCollection(usosResult.value));
      }

      if (formasResult.status === 'fulfilled') {
        this.paymentFormOptions = normalizeFiscalCodeOptions(extractCollection(formasResult.value));
      }

      if (metodosResult.status === 'fulfilled') {
        this.paymentMethodOptions = normalizeFiscalCodeOptions(extractCollection(metodosResult.value));
      }

      if (globalPeriodicitiesResult.status === 'fulfilled') {
        this.globalInvoicePeriodicityOptions = normalizeGlobalInvoicePeriodicityOptions(
          extractCollection(globalPeriodicitiesResult.value)
        );
      }

      if (this.paymentMethodOptions.length > 0) {
        const ppdOption = this.paymentMethodOptions.find((item) => String(item?.value || '') === 'PPD');
        this.invoiceInfo = {
          ...this.invoiceInfo,
          paymentMethod: ppdOption?.value || this.paymentMethodOptions[0]?.value || 'PPD',
        };
      }

      if (this.paymentFormOptions.length > 0) {
        const ninetyNineOption = this.paymentFormOptions.find((item) => String(item?.value || '') === '99');
        this.invoiceInfo = {
          ...this.invoiceInfo,
          paymentForm: ninetyNineOption?.value || this.paymentFormOptions[0]?.value || '99',
        };
      }

      this.invoiceInfo = {
        ...this.invoiceInfo,
        globalPeriodicity: this.resolveDefaultGlobalPeriodicityCode(),
        globalExercise: this.resolveCurrentGlobalExerciseValue(),
      };
    } catch (error) {
      // Keep local fallbacks in receiver card when catalogs are unavailable.
    }
  },
  async loadIssuerTaxInformation() {
    try {
      const response = await accountService.getTaxInformation();
      const payload = extractPayload(response);
      const data = payload && typeof payload === 'object' && payload.data && typeof payload.data === 'object'
        ? payload.data
        : payload;

      const businessName = String(data?.business_name || data?.name || '').trim();
      const rfc = String(data?.rfc || '').trim();
      if (businessName === '' && rfc === '') {
        return;
      }

      this.issuer = {
        ...this.issuer,
        businessName: businessName !== '' ? businessName : this.issuer.businessName,
        rfc: rfc !== '' ? rfc : this.issuer.rfc,
        taxRegime: String(data?.regimen_label || data?.regimen || '').trim() || this.issuer.taxRegime,
        fiscalResidence: String(data?.fiscal_residence || data?.zipcode || '').trim() || this.issuer.fiscalResidence,
      };
    } catch (error) {
      // Keep fallback issuer data when service is unavailable.
    }
  },
  async loadReceiverSelectors() {
    try {
      const response = await invoicesService.receiverSelectors();
      const items = extractCollection(response);
      if (!Array.isArray(items) || items.length === 0) {
        return;
      }

      const mapped = items
        .map((item) => mapCustomerOption({
          id: Number(item?.contributor_id || item?.id || 0),
          customerId: Number(item?.customer_id || item?.company_id || 0),
          default: Number(item?.default || 0) === 1 ? 1 : 0,
          clientName: String(item?.client_name || '').trim(),
          rfc: String(item?.rfc || '').trim(),
          businessName: String(item?.business_name || item?.name || '').trim(),
          cfdiUseCode: String(item?.cfdi_use || '').trim(),
          cfdiUse: String(item?.cfdi_use_label || item?.cfdi_use || '').trim(),
          taxRegimeCode: String(item?.tax_regime || '').trim(),
          taxRegime: String(item?.tax_regime_label || item?.tax_regime || '').trim(),
          fiscalResidence: String(item?.fiscal_residence || '').trim(),
        }))
        .filter((option) => Number(option?.value || 0) > 0 && String(option?.label || '').trim() !== '');

      if (mapped.length > 0) {
        this.customerOptions = mapped;
      }
    } catch (error) {
      // Keep fallback receiver options when service is unavailable.
    }
  },
  async loadSerialSelectors() {
    try {
      const response = await invoicesService.serialSelectors({ document_type: 'I' });
      const items = extractCollection(response);
      const mapped = (Array.isArray(items) ? items : [])
        .map((item) => mapSerialOption(item))
        .filter((option) => Number(option?.value || 0) > 0 && String(option?.label || '').trim() !== '');

      if (mapped.length === 0) {
        return;
      }

      this.serialOptions = mapped;

      const first = mapped[0];
      this.invoiceInfo = {
        ...this.invoiceInfo,
        serialId: first?.value || '',
        series: first?.label || this.invoiceInfo.series,
        folio: String(first?.meta?.folio ?? this.invoiceInfo.folio),
      };
    } catch (error) {
      // Keep fallback static series when service is unavailable.
    }
  },
  onHeaderAction(action) {
    const actionKey = String(action?.key || '').trim();
    if (actionKey === 'back') {
      this.goBack();
    }
  },
  onInvoiceInfoChange(payload) {
    const key = String(payload?.key || '').trim();
    if (key === '') {
      return;
    }

    const nextInvoiceInfo = {
      ...this.invoiceInfo,
      [key]: payload?.value,
    };

    if (key === 'date' && this.isPublicGeneralReceiver() && Number(nextInvoiceInfo?.isGlobal || 0) !== 1) {
      nextInvoiceInfo.globalExercise = this.resolveCurrentGlobalExerciseValue(nextInvoiceInfo?.date);
      nextInvoiceInfo.globalPeriodicity = this.resolveDefaultGlobalPeriodicityCode();
    }

    this.invoiceInfo = nextInvoiceInfo;
  },
  onCommentsChange(value) {
    this.comments = String(value || '');
  },
  selectReceiver(customer) {
    this.selectedReceiver = customer && typeof customer === 'object' ? customer : null;
    this.syncGlobalInvoiceByReceiver(this.selectedReceiver);
  },
  async createInlineCustomer(payload) {
    const inlineData = payload && typeof payload === 'object' ? payload : {};
    const inlineCustomerId = Number(
      inlineData?.customerId
      || this.prefillPaymentCustomerId
      || this.resolveSelectedCompanyId()
      || 0
    ) || 0;

    if (this.inlineBillingDataOnly && inlineCustomerId > 0) {
      try {
        const response = await customersService.contributorsStore(inlineCustomerId, {
          name: String(inlineData?.businessName || '').trim(),
          rfc: String(inlineData?.rfc || '').trim().toUpperCase(),
          regimen: String(inlineData?.taxRegimeCode || '').trim(),
          uso_cfdi: String(inlineData?.cfdiUseCode || '').trim(),
          zipcode: String(inlineData?.fiscalResidence || '').trim(),
          payment_method: '99',
          payment_method_type: 'PPD',
          type: 0,
          default: 1,
          active: 1,
        });

        const contributorPayload = extractPayload(response);
        const createdContributorId = Number(
          contributorPayload?.id
          || contributorPayload?.contributor_id
          || 0
        ) || 0;

        await this.loadReceiverSelectors();

        const selectedById = createdContributorId > 0
          ? this.customerOptions.find((option) => {
            const optionId = Number(option?.meta?.id || option?.value || 0) || 0;
            const optionCustomerId = Number(option?.meta?.customerId || option?.customerId || 0) || 0;
            return optionId === createdContributorId && optionCustomerId === inlineCustomerId;
          }) || null
          : null;

        if (selectedById) {
          this.selectedReceiver = selectedById;
          this.syncGlobalInvoiceByReceiver(selectedById);
          this.receiverLocked = true;
        } else {
          const receiverResolved = await this.ensureReceiverSelectedByCustomer(inlineCustomerId);
          this.receiverLocked = receiverResolved;
        }

        this.$toast?.success(this.text.create.toast.inlineBillingDataCreated || this.text.create.toast.inlineCustomerCreated);
      } catch (error) {
        const message = error?.response?.data?.message
          || this.text.create.toast.inlineBillingDataCreateError
          || this.text.create.toast.inlineCustomerCreateError;
        this.$toast?.error(message);
      }

      return;
    }

    try {
      const response = await customersService.storeWithContributor({
        client_name: String(inlineData?.clientName || '').trim(),
        business_name: String(inlineData?.businessName || '').trim(),
        rfc: String(inlineData?.rfc || '').trim().toUpperCase(),
        tax_regime: String(inlineData?.taxRegimeCode || '').trim(),
        cfdi_use: String(inlineData?.cfdiUseCode || '').trim(),
        fiscal_residence: String(inlineData?.fiscalResidence || '').trim(),
      });

      const payloadData = extractPayload(response);
      const data = payloadData && typeof payloadData === 'object' && payloadData.data && typeof payloadData.data === 'object'
        ? payloadData.data
        : payloadData;

      const customer = data?.customer || {};
      const contributor = data?.contributor || {};

      const option = mapCustomerOption({
        id: Number(contributor?.id || 0),
        customerId: Number(customer?.id || contributor?.company_id || 0),
        clientName: String(customer?.name || inlineData?.clientName || '').trim(),
        rfc: String(contributor?.rfc || inlineData?.rfc || '').trim().toUpperCase(),
        businessName: String(contributor?.name || inlineData?.businessName || '').trim(),
        cfdiUseCode: String(contributor?.uso_cfdi || inlineData?.cfdiUseCode || '').trim(),
        cfdiUse: String(contributor?.uso_cfdi_label || inlineData?.cfdiUseLabel || contributor?.uso_cfdi || '').trim(),
        taxRegimeCode: String(contributor?.regimen || inlineData?.taxRegimeCode || '').trim(),
        taxRegime: String(contributor?.regimen_label || inlineData?.taxRegimeLabel || contributor?.regimen || '').trim(),
        fiscalResidence: String(contributor?.zipcode || inlineData?.fiscalResidence || '').trim(),
      });

      this.customerOptions = [...this.customerOptions, option]
        .sort((left, right) => String(left?.label || '').localeCompare(String(right?.label || ''), 'es'));
      this.selectedReceiver = option;
      this.syncGlobalInvoiceByReceiver(option);
      this.$toast?.success(this.text.create.toast.inlineCustomerCreated);
    } catch (error) {
      const message = error?.response?.data?.message || this.text.create.toast.inlineCustomerCreateError;
      this.$toast?.error(message);
    }
  },
  addConcept() {
    this.conceptModalMode = 'create';
    this.editingConceptIndex = -1;
    this.showConceptModal = true;
  },
  editConcept(payload) {
    const index = Number(payload?.index);
    if (!Number.isFinite(index) || index < 0 || index >= this.concepts.length) {
      return;
    }

    this.conceptModalMode = 'edit';
    this.editingConceptIndex = index;
    this.showConceptModal = true;
  },
  deleteConcept(index) {
    const normalizedIndex = Number(index);
    if (!Number.isFinite(normalizedIndex) || normalizedIndex < 0 || normalizedIndex >= this.concepts.length) {
      return;
    }

    this.concepts = this.concepts.filter((_, rowIndex) => rowIndex !== normalizedIndex);
  },
  closeConceptModal() {
    this.showConceptModal = false;
    this.conceptModalMode = 'create';
    this.editingConceptIndex = -1;
  },
  extractPaymentPlanIdsFromConcept(concept = {}) {
    const explicitIds = Array.isArray(concept?.sourcePaymentIds)
      ? concept.sourcePaymentIds
      : [];
    const parsedExplicit = explicitIds
      .map((value) => Number(value || 0) || 0)
      .filter((value) => value > 0);

    if (parsedExplicit.length > 0) {
      return [...new Set(parsedExplicit)];
    }

    const paymentId = Number(concept?.paymentId || concept?.payment_id || 0) || 0;
    return paymentId > 0 ? [paymentId] : [];
  },
  isPaymentPlanConcept(concept = {}) {
    const sourceMode = String(concept?.sourceMode || '').trim().toLowerCase();
    if (sourceMode === 'payment-plan') {
      return true;
    }

    return this.extractPaymentPlanIdsFromConcept(concept).length > 0;
  },
  submitConceptFromModal(conceptPayload) {
    const incomingConcepts = Array.isArray(conceptPayload?.concepts)
      ? conceptPayload.concepts
      : [conceptPayload];
    const payloadSourceMode = String(conceptPayload?.sourceMode || '').trim().toLowerCase();

    const normalizedConcepts = incomingConcepts
      .map((item) => {
        const sourcePaymentIds = Array.isArray(item?.sourcePaymentIds)
          ? [...new Set(item.sourcePaymentIds
            .map((value) => Number(value || 0) || 0)
            .filter((value) => value > 0))]
          : [];

        return {
          serviceId: Number(item?.serviceId || item?.productId || 0) || null,
          productId: Number(item?.productId || item?.serviceId || 0) || null,
          paymentId: Number(item?.paymentId || item?.payment_id || 0) || 0,
          saleId: Number(item?.saleId || item?.sale_id || 0) || 0,
          qty: Number(item?.qty || 0) || 1,
          unit: String(item?.unit || '').trim(),
          unitValue: String(item?.unitValue || '').trim(),
          unitCode: String(item?.unitCode || '').trim(),
          unitId: Number(item?.unitId || 0) || 0,
          code: String(item?.code || '').trim(),
          description: String(item?.description || '').trim(),
          unitPrice: Number(item?.unitPrice || 0) || 0,
          taxLabel: String(item?.taxLabel || '').trim() || this.generalText.financial.taxes.defaultTransferredLabel,
          taxAmount: Number(item?.taxAmount || 0) || 0,
          taxTransferredTotal: Number(item?.taxTransferredTotal || 0) || 0,
          taxWithheldTotal: Number(item?.taxWithheldTotal || 0) || 0,
          taxId: Number(item?.taxId || 0) || 0,
          taxGroupId: Number(item?.taxGroupId || 0) || 0,
          sourceMode: String(item?.sourceMode || payloadSourceMode || '').trim().toLowerCase(),
          sourcePaymentIds,
          isPaymentPlanMerged: Boolean(item?.isPaymentPlanMerged),
        };
      })
      .filter((item) => {
        return Number(item?.unitPrice || 0) > 0
          || String(item?.description || '').trim() !== ''
          || Number(item?.productId || 0) > 0
          || Number(item?.paymentId || 0) > 0;
      });

    if (normalizedConcepts.length === 0) {
      return;
    }

    if (payloadSourceMode === 'payment-plan') {
      const retainedConcepts = this.concepts.filter((concept) => !this.isPaymentPlanConcept(concept));
      this.concepts = [...retainedConcepts, ...normalizedConcepts];
      this.showConceptModal = false;
      this.conceptModalMode = 'create';
      this.editingConceptIndex = -1;
      return;
    }

    if (this.conceptModalMode === 'edit' && this.editingConceptIndex >= 0) {
      const [firstConcept, ...restConcepts] = normalizedConcepts;
      const currentConcept = this.concepts[this.editingConceptIndex] || {};
      const currentSourcePaymentIds = this.extractPaymentPlanIdsFromConcept(currentConcept);
      const nextSourcePaymentIds = Array.isArray(firstConcept?.sourcePaymentIds)
        ? firstConcept.sourcePaymentIds
        : [];
      const mergedFirstConcept = {
        ...currentConcept,
        ...firstConcept,
        sourceMode: String(firstConcept?.sourceMode || currentConcept?.sourceMode || '').trim().toLowerCase(),
        sourcePaymentIds: nextSourcePaymentIds.length > 0 ? nextSourcePaymentIds : currentSourcePaymentIds,
        isPaymentPlanMerged: Boolean(
          firstConcept?.isPaymentPlanMerged
          || currentConcept?.isPaymentPlanMerged
          || (nextSourcePaymentIds.length > 1)
          || (currentSourcePaymentIds.length > 1)
        ),
      };

      this.concepts = this.concepts.map((item, index) => {
        return index === this.editingConceptIndex ? mergedFirstConcept : item;
      });

      if (restConcepts.length > 0) {
        this.concepts = [...this.concepts, ...restConcepts];
      }
    } else {
      this.concepts = [...this.concepts, ...normalizedConcepts];
    }

    this.showConceptModal = false;
    this.conceptModalMode = 'create';
    this.editingConceptIndex = -1;
  },
  resetDraft() {
    this.concepts = [];
    this.$toast?.success(this.text.create.toast.conceptsCleared);
  },
  previewInvoice() {
    this.$toast?.info(this.text.create.toast.previewPending);
  },
  resolveSelectedContributorId() {
    return Number(this.selectedReceiver?.meta?.id || this.selectedReceiver?.value || this.selectedReceiver?.id || 0) || 0;
  },
  resolveSelectedCompanyId() {
    return Number(
      this.selectedReceiver?.meta?.customerId
      || this.selectedReceiver?.customerId
      || this.selectedReceiver?.meta?.companyId
      || this.selectedReceiver?.companyId
      || 0
    ) || 0;
  },
  buildConceptsPayload() {
    return this.concepts.map((concept) => {
      const quantity = Number(concept?.qty || 0) || 0;
      const unitPrice = Number(concept?.unitPrice || 0) || 0;
      const unitValue = String(concept?.unitValue || '').trim();
      const unitCode = String(concept?.unitCode || '').trim() || (Number.isFinite(Number(unitValue)) ? '' : unitValue);
      const unitId = Number(concept?.unitId || unitValue || 0) || 0;
      const sourcePaymentIds = Array.isArray(concept?.sourcePaymentIds)
        ? [...new Set(concept.sourcePaymentIds
          .map((value) => Number(value || 0) || 0)
          .filter((value) => value > 0))]
        : [];

      return {
        quantity,
        price: unitPrice,
        import: quantity * unitPrice,
        discount: 0,
        unit_code: unitCode,
        unit_id: unitId,
        sat_code: String(concept?.code || '').trim(),
        description: String(concept?.description || '').trim(),
        product_id: Number(concept?.productId || 0) || 0,
        tax_id: Number(concept?.taxId || 0) || 0,
        tax_group_id: Number(concept?.taxGroupId || 0) || 0,
        payment_id: Number(concept?.paymentId || concept?.payment_id || 0) || 0,
        source_payment_ids: sourcePaymentIds,
        sale_id: Number(concept?.saleId || concept?.sale_id || 0) || 0,
      };
    });
  },
  createIdempotencyKey() {
    if (typeof globalThis.crypto?.randomUUID === 'function') {
      return globalThis.crypto.randomUUID();
    }

    return `invoice-create-${Date.now()}-${Math.round(Math.random() * 1000000)}`;
  },
  buildInvoiceSnapshot(responseData) {
    return {
      id: Number(responseData?.id || 0) || 0,
      serial: String(responseData?.serial || responseData?.cfdi?.serie || '').trim(),
      folio: String(responseData?.folio || responseData?.cfdi?.folio || '').trim(),
      date: String(this.invoiceInfo?.date || responseData?.date || '').trim(),
      serialId: Number(this.invoiceInfo?.serialId || 0) || 0,
      paymentMethod: String(this.invoiceInfo?.paymentMethod || '').trim(),
      paymentForm: String(this.invoiceInfo?.paymentForm || '').trim(),
      issuer: { ...this.issuer },
      selectedReceiver: this.selectedReceiver ? { ...this.selectedReceiver } : null,
      concepts: Array.isArray(this.concepts) ? this.concepts.map((concept) => ({ ...concept })) : [],
      comments: String(this.comments || '').trim(),
      currency: String(this.currency || 'MXN').trim().toUpperCase(),
    };
  },
  persistInvoiceSnapshot(snapshot) {
    const invoiceId = Number(snapshot?.id || 0) || 0;
    if (invoiceId <= 0) {
      return;
    }

    try {
      sessionStorage.setItem(`invoice-detail-${invoiceId}`, JSON.stringify(snapshot));
    } catch (error) {
      // Ignore storage quota/access errors.
    }
  },
  async submitInvoice() {
    if (this.submittingInvoice) {
      return;
    }

    const contributorId = this.resolveSelectedContributorId();
    const companyId = this.resolveSelectedCompanyId();
    const serialId = Number(this.invoiceInfo?.serialId || 0) || 0;

    if (companyId <= 0) {
      this.$toast?.error(this.text.create.validation.companyRequired);
      return;
    }

    if (contributorId <= 0) {
      this.$toast?.error(this.text.create.validation.contributorRequired);
      return;
    }

    if (serialId <= 0) {
      this.$toast?.error(this.text.create.validation.serialRequired);
      return;
    }

    if (!Array.isArray(this.concepts) || this.concepts.length === 0) {
      this.$toast?.error(this.text.create.validation.conceptsRequired);
      return;
    }

    const conceptsPayload = this.buildConceptsPayload();
    if (conceptsPayload.some((concept) => String(concept?.unit_code || '').trim() === '')) {
      this.$toast?.error(this.text.create.validation.unitCodeRequired);
      return;
    }

    if (conceptsPayload.some((concept) => String(concept?.sat_code || '').trim() === '')) {
      this.$toast?.error(this.text.create.validation.satCodeRequired);
      return;
    }

    const idempotencyKey = this.createIdempotencyKey();
    const usoCfdi = String(this.selectedReceiver?.meta?.cfdiUseCode || '').trim();
    const receiverRfc = String(this.selectedReceiver?.meta?.rfc || this.selectedReceiver?.rfc || '').trim().toUpperCase();
    const isPublicGeneralReceiver = receiverRfc === GLOBAL_PUBLIC_RFC;
    const isGlobalSelected = Number(this.invoiceInfo?.isGlobal || 0) === 1;
    const shouldApplyGlobalInvoice = isPublicGeneralReceiver || isGlobalSelected;
    const defaultGlobalExercise = this.resolveCurrentGlobalExerciseValue(this.invoiceInfo?.date);
    const globalExercise = String(
      (isPublicGeneralReceiver && !isGlobalSelected)
        ? defaultGlobalExercise
        : (this.invoiceInfo?.globalExercise || defaultGlobalExercise)
    ).trim();
    const [globalYearRaw, globalMonthRaw] = globalExercise.split('-');
    const globalYear = Number(globalYearRaw || 0) || 0;
    const globalMonth = String(globalMonthRaw || '').padStart(2, '0');
    const globalPeriodicity = String(
      (isPublicGeneralReceiver && !isGlobalSelected)
        ? this.resolveDefaultGlobalPeriodicityCode()
        : (this.invoiceInfo?.globalPeriodicity || this.resolveDefaultGlobalPeriodicityCode())
    ).trim() || this.resolveDefaultGlobalPeriodicityCode();
    const hasLinkedConcepts = conceptsPayload.some((concept) => {
      return Number(concept?.payment_id || 0) > 0 || Number(concept?.sale_id || 0) > 0;
    });
    const payload = {
      type: hasLinkedConcepts ? 'normal' : 'generic',
      document_type: 'I',
      date: String(this.invoiceInfo?.date || '').trim(),
      company_id: companyId,
      serial_id: serialId,
      receptor_rfc_id: contributorId,
      uso_cfdi: usoCfdi,
      payment_method: String(this.invoiceInfo?.paymentMethod || 'PPD').trim(),
      payment_form: String(this.invoiceInfo?.paymentForm || '99').trim(),
      currency: String(this.currency || 'MXN').trim().toUpperCase(),
      exchange_rate: 1,
      comments: String(this.comments || '').trim(),
      concepts: conceptsPayload,
      is_global_invoice: shouldApplyGlobalInvoice ? 1 : 0,
    };

    if (shouldApplyGlobalInvoice) {
      payload.global_periodicity = globalPeriodicity;
      payload.global_exercise = globalExercise;
      payload.global_year = globalYear;
      payload.global_months = globalMonth;
    }

    this.submittingInvoice = true;
    try {
      const response = await invoicesService.store(payload, {
        headers: {
          'Idempotency-Key': idempotencyKey,
        },
      });

      const apiErrorMessage = extractApiErrorMessage(response, '');
      if (apiErrorMessage !== '') {
        this.$toast?.error(apiErrorMessage);
        return;
      }

      const data = extractPayload(response);
      const serial = String(data?.serial || data?.cfdi?.serie || '').trim();
      const folio = String(data?.folio || data?.cfdi?.folio || '').trim();
      const reference = `${serial}${folio}`.trim() || this.text.create.toast.invoiceReferenceFallback;
      const snapshot = this.buildInvoiceSnapshot(data);
      const invoiceId = Number(data?.id || snapshot?.id || 0) || 0;

      this.persistInvoiceSnapshot(snapshot);

      try {
        sessionStorage.setItem('app.breadcrumb.invoice-detail', JSON.stringify({
          contextId: String(invoiceId || Date.now()),
          source: 'invoices',
          saleId: 0,
          saleName: '',
          customerId: Number(snapshot?.selectedReceiver?.meta?.customerId || snapshot?.selectedReceiver?.customerId || 0) || 0,
          customerName: String(
            snapshot?.selectedReceiver?.meta?.clientName
            || snapshot?.selectedReceiver?.label
            || ''
          ).trim(),
        }));
      } catch (error) {
        // Keep redirect flow even if session storage is unavailable.
      }

      const successMessage = this.text.create.toast.invoiceCreated(reference);
      this.$toast?.success(successMessage);
      this.$router.push({
        name: 'invoiceDetail',
        params: {
          invoiceId: String(invoiceId || Date.now()),
        },
        state: {
          invoiceSnapshot: snapshot,
        },
      });
    } catch (error) {
      const fallback = this.text.create.toast.invoiceCreateError;
      const message = extractApiErrorMessage(error?.response || error, fallback);
      this.$toast?.error(message || fallback);
    } finally {
      this.submittingInvoice = false;
    }
  },
  goBack() {
    this.$router.push({ name: 'invoices' });
  },
};
